// utils/analysisHelpers.js

/**
 * Safely converts a value to a number, returning 0 if invalid
 * @param {any} val - The value to convert
 * @returns {number} The converted number or 0
 */
export function toSafeNum(val) {
  const num = Number(val);
  return isNaN(num) ? 0 : num;
}

/**
 * Gets the style object for a group based on its status
 * @param {boolean} isNew - Whether the group is new
 * @param {boolean} isLost - Whether the group is lost
 * @param {number} posChange - The position change
 * @returns {Object} Style object for the group
 */
export function getStyleForGroup(isNew, isLost, posChange) {
  if (isNew) {
    return { color: '#10B981' }; // Green
  }
  if (isLost) {
    return { color: '#EF4444' }; // Red
  }
  if (posChange > 0) {
    return { color: '#3B82F6' }; // Blue
  }
  if (posChange < 0) {
    return { color: '#F59E0B' }; // Yellow
  }
  return {};
}

/**
 * Gets the icon for a group based on its status
 * @param {boolean} isNew - Whether the group is new
 * @param {boolean} isLost - Whether the group is lost
 * @param {number} posChange - The position change
 * @returns {string} Icon for the group
 */
export function getIconForGroup(isNew, isLost, posChange) {
  if (isNew) return '🆕';
  if (isLost) return '🚫';
  if (posChange > 0) return '⬆️';
  if (posChange < 0) return '⬇️';
  return '➡️';
}

/**
 * Formats a number for display
 * @param {number} num - The number to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number
 */
export function formatNumber(num, decimals = 0) {
  return Number(num).toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Calculates the percentage change between two numbers
 * @param {number} oldVal - The old value
 * @param {number} newVal - The new value
 * @returns {string} Formatted percentage change
 */
export function getPercentageChange(oldVal, newVal) {
  const old = toSafeNum(oldVal);
  const new_ = toSafeNum(newVal);
  
  if (old === 0) return new_ > 0 ? '+∞' : '0%';
  
  const change = ((new_ - old) / old) * 100;
  return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
}

/**
 * Determines if a change is significant based on a threshold
 * @param {number} oldVal - The old value
 * @param {number} newVal - The new value
 * @param {number} threshold - The threshold percentage (0-1)
 * @returns {boolean} Whether the change is significant
 */
export function isSignificantChange(oldVal, newVal, threshold = 0.1) {
  const old = toSafeNum(oldVal);
  const new_ = toSafeNum(newVal);
  
  if (old === 0) return new_ > 0;
  
  const percentChange = Math.abs((new_ - old) / old);
  return percentChange >= threshold;
}

/**
 * Flattens all 'keywordDetails' arrays from the 'analysis' object into a single array
 * @param {Object} analysis - The analysis object
 * @returns {Array} Array of all keywords
 */
export function getAllKeywords(analysis) {
  const all = [];
  Object.entries(analysis).forEach(([, groupDetails]) => {
    const kwArr = groupDetails?.keywordDetails || [];
    kwArr.forEach(k => all.push(k));
  });
  return all;
}

/**
 * Flattens all 'keywordDetails' arrays from the 'analysis' object into a single array
 * and includes the topic/page information
 * @param {Object} analysis - The analysis object
 * @returns {Array} Array of all keywords with topic information
 */
export function getAllKeywordsWithTopic(analysis) {
  const all = [];
  Object.entries(analysis).forEach(([topic, groupDetails]) => {
    const kwArr = groupDetails?.keywordDetails || [];
    kwArr.forEach(k => all.push({
      ...k,
      topic: topic // Add the topic (page URL) from the parent
    }));
  });
  return all;
}

/**
 * Takes a URL string and returns the path, search params, and hash.
 * If it's just the base domain, it returns "Homepage".
 * @param {string} url - The URL to process
 * @returns {string} The path part of the URL or "Homepage"
 */
export function stripBaseDomain(url) {
  try {
    if (!url || typeof url !== 'string' || !url.startsWith('http')) return url || 'N/A';
    const urlObj = new URL(url);
    const path = urlObj.pathname + urlObj.search + urlObj.hash;
    return (path === '/' || path === '') ? 'Homepage' : path;
  } catch (e) {
    // console.warn(\`Error stripping base domain from URL: \${url}\`, e); // Optional: for debugging
    return url; // Return original URL if parsing fails
  }
}

/**
 * Sorts an analysis object by the given sort type and returns a sorted array.
 * @param {Object} analysis - The analysis object to sort.
 * @param {string} sortType - The type of sorting to apply (e.g., 'performance', 'clicks').
 * @returns {Array} A sorted array of [key, value] pairs from the analysis object.
 */
export function getSortedGroups(analysis, sortType) {
  if (!analysis || typeof analysis !== 'object') return [];
  const arr = Object.entries(analysis);

  // Ensure toSafeNum is accessible here. If it's in the same file, it will be.
  // If toSafeNum were imported from elsewhere, ensure that import is present.

  switch (sortType) {
    case 'performance':
      arr.sort((a, b) => toSafeNum(b[1].performanceScore) - toSafeNum(a[1].performanceScore));
      break;
    case 'clicks':
      arr.sort((a, b) => toSafeNum(b[1].c2) - toSafeNum(a[1].c2));  // clicksNew → c2
      break;
    case 'click_increase':
      arr.sort((a, b) => {
        const changeA = toSafeNum(a[1].c2) - toSafeNum(a[1].c1);  // clicksNew/Old → c2/c1
        const changeB = toSafeNum(b[1].c2) - toSafeNum(b[1].c1);  // clicksNew/Old → c2/c1
        return changeB - changeA;
      });
      break;
    case 'click_decline':
      arr.sort((a, b) => {
        const changeA = toSafeNum(a[1].c1) - toSafeNum(a[1].c2);  // clicksOld/New → c1/c2
        const changeB = toSafeNum(b[1].c1) - toSafeNum(b[1].c2);  // clicksOld/New → c1/c2
        return changeB - changeA;
      });
      break;
    case 'impressions':
      arr.sort((a, b) => toSafeNum(b[1].i2) - toSafeNum(a[1].i2));  // impressionsNew → i2
      break;
    case 'impression_increase':
      arr.sort((a, b) => {
        const changeA = toSafeNum(a[1].i2) - toSafeNum(a[1].i1);  // impressionsNew/Old → i2/i1
        const changeB = toSafeNum(b[1].i2) - toSafeNum(b[1].i1);  // impressionsNew/Old → i2/i1
        return changeB - changeA;
      });
      break;
    case 'impression_decline':
      arr.sort((a, b) => {
        const changeA = toSafeNum(a[1].i1) - toSafeNum(a[1].i2);  // impressionsOld/New → i1/i2
        const changeB = toSafeNum(b[1].i1) - toSafeNum(b[1].i2);  // impressionsOld/New → i1/i2
        return changeB - changeA;
      });
      break;
    default: // alphabetical or any other unhandled case
      arr.sort((a, b) => String(a[0]).localeCompare(String(b[0])));
      break;
  }
  return arr;
}

/**
 * Scrolls the page to the top, prioritizing specific target refs before falling back to window scroll.
 * @param {React.RefObject} primaryTargetRef - The primary ref to scroll into view.
 * @param {React.RefObject} fallbackContainerRef - The fallback container ref to scroll its top.
 */
export function scrollToTop(primaryTargetRef, fallbackContainerRef) {
  if (primaryTargetRef && primaryTargetRef.current) {
    primaryTargetRef.current.scrollIntoView({ behavior: 'smooth' });
  } else if (fallbackContainerRef && fallbackContainerRef.current) {
    fallbackContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    console.warn('scrollToTop util: primaryTargetRef not available, using fallbackContainerRef.scrollTo.');
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    console.warn('scrollToTop util: Neither primaryTargetRef nor fallbackContainerRef available.');
  }
}

/**
 * Formats a date range intelligently based on whether dates cross months/years
 * @param {string|Date} startDate - The start date
 * @param {string|Date} endDate - The end date
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeYear - Force include year even if current year
 * @param {boolean} options.shortYear - Use 2-digit year format ('25 vs 2025)
 * @returns {string} Formatted date range
 */
export function formatSmartDateRange(startDate, endDate, options = {}) {
  if (!startDate || !endDate) return '';
  
  const { includeYear = false, shortYear = true } = options;
  
  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const currentYear = new Date().getFullYear();
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return `${startDate} - ${endDate}`; // Fallback to original strings
    }
    
    const startMonth = start.getMonth();
    const endMonth = end.getMonth();
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();
    const startDay = start.getDate();
    const endDay = end.getDate();
    
    // Month abbreviations
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Helper to format year
    const formatYear = (year) => {
      if (shortYear) {
        return `'${year.toString().slice(-2)}`;
      }
      return year.toString();
    };
    
    // Helper to determine if we should show year
    const shouldShowYear = (year) => {
      return includeYear || year !== currentYear;
    };
    
    // Same month and year
    if (startMonth === endMonth && startYear === endYear) {
      const yearStr = shouldShowYear(startYear) ? `, ${formatYear(startYear)}` : '';
      return `${months[startMonth]} ${startDay}-${endDay}${yearStr}`;
    }
    
    // Different months, same year
    if (startYear === endYear) {
      const yearStr = shouldShowYear(startYear) ? `, ${formatYear(startYear)}` : '';
      return `${months[startMonth]} ${startDay} - ${months[endMonth]} ${endDay}${yearStr}`;
    }
    
    // Different years
    const startYearStr = shouldShowYear(startYear) ? `, ${formatYear(startYear)}` : '';
    const endYearStr = shouldShowYear(endYear) ? `, ${formatYear(endYear)}` : '';
    return `${months[startMonth]} ${startDay}${startYearStr} - ${months[endMonth]} ${endDay}${endYearStr}`;
    
  } catch (error) {
    console.warn('Error formatting date range:', error);
    return `${startDate} - ${endDate}`; // Fallback to original strings
  }
}

/**
 * Formats two date ranges with "vs" separator and optional color styling info
 * @param {Object} period1 - First period with start/end dates
 * @param {Object} period2 - Second period with start/end dates  
 * @param {Object} options - Formatting options
 * @param {string} options.separator - Separator between periods (default: "vs")
 * @param {boolean} options.includeLabels - Include "Recent:" and "Compare:" labels
 * @param {boolean} options.shortYear - Use 2-digit year format
 * @returns {Object} Formatted comparison with text and styling info
 */
export function formatDateComparison(period1, period2, options = {}) {
  const { 
    separator = 'vs', 
    includeLabels = false, 
    shortYear = true 
  } = options;
  
  if (!period1 || !period2) return { text: '', period1Text: '', period2Text: '' };
  
  const period1Text = formatSmartDateRange(period1.start || period1.startDate, 
                                          period1.end || period1.endDate, 
                                          { shortYear });
  const period2Text = formatSmartDateRange(period2.start || period2.startDate, 
                                          period2.end || period2.endDate, 
                                          { shortYear });
  
  if (includeLabels) {
    return {
      text: `Recent: ${period1Text} ${separator} Compare: ${period2Text}`,
      period1Text: `Recent: ${period1Text}`,
      period2Text: `Compare: ${period2Text}`,
      separator
    };
  }
  
  return {
    text: `${period1Text} ${separator} ${period2Text}`,
    period1Text,
    period2Text,
    separator
  };
}

/**
 * Calculate min/max bounds for numeric fields in analysis data
 * @param {Object} analysis - Analysis data object
 * @param {string} fieldName - Field name to extract (e.g., 'impressionsNew', 'clicksNew')
 * @param {Object} options - Configuration options
 * @param {number} options.defaultMin - Default minimum value
 * @param {number} options.defaultMax - Default maximum value
 * @param {number} options.minRoundingFactor - Rounding factor for minimum (e.g., 10)
 * @param {number} options.maxRoundingFactor - Rounding factor for maximum (e.g., 100)
 * @param {boolean} options.includeZero - Whether to include zero values
 * @returns {Object} Bounds object with min and max properties
 */
export const calculateFieldBounds = (analysis, fieldName, options = {}) => {
  const {
    defaultMin = 0,
    defaultMax = 1000,
    minRoundingFactor = 10,
    maxRoundingFactor = 100,
    includeZero = false
  } = options;

  if (!analysis || typeof analysis !== 'object') {
    return { min: defaultMin, max: defaultMax };
  }
  
  const values = Object.values(analysis)
    .map(details => toSafeNum(details?.[fieldName]) || 0)
    .filter(val => includeZero ? val >= 0 : val > 0);
  
  if (values.length === 0) {
    return { min: defaultMin, max: defaultMax };
  }
  
  const min = Math.min(...values);
  const max = Math.max(...values);
  
  // Round to nice numbers
  const roundedMin = Math.max(0, Math.floor(min / minRoundingFactor) * minRoundingFactor);
  const roundedMax = Math.ceil(max / maxRoundingFactor) * maxRoundingFactor;
  
  return { 
    min: roundedMin, 
    max: Math.max(roundedMax, roundedMin + minRoundingFactor) 
  };
};

/**
 * Calculate impressions bounds from analysis data
 * @param {Object} analysis - Analysis data object
 * @returns {Object} Bounds object with min and max properties
 */
export const calculateImpressionsBounds = (analysis) => {
  return calculateFieldBounds(analysis, 'i2', {  // impressionsNew → i2
    defaultMin: 0,
    defaultMax: 10000,
    minRoundingFactor: 10,
    maxRoundingFactor: 100,
    includeZero: true
  });
};

/**
 * Calculate clicks bounds from analysis data
 * @param {Object} analysis - Analysis data object
 * @returns {Object} Bounds object with min and max properties
 */
export const calculateClicksBounds = (analysis) => {
  return calculateFieldBounds(analysis, 'c2', {  // clicksNew → c2
    defaultMin: 0,
    defaultMax: 1000,
    minRoundingFactor: 1,
    maxRoundingFactor: 10,
    includeZero: true
  });
};